import { watchlistApi } from "shared/api/client/orval";
import type { Asset } from "features/catalog/model/asset.model";

export interface WatchlistDto {
  id: number;
  title: string;
  language: string;
  uploadTime: string;
  price: number;
  status: string;
  userId: number;
  videoIds: number[];
  imageIds: number[];
  subtitleIds: number[];
  addedAt: string;
  assetUploadId: number;
}

function toInt(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function toStringValue(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function toIntArray(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => toInt(item)).filter((item) => item > 0);
}

function mapWatchlistRow(userId: number, row: unknown): WatchlistDto {
  const source = (row ?? {}) as Record<string, unknown>;
  const assetUploadId = toInt(source.id ?? source.ID);

  return {
    id: assetUploadId,
    assetUploadId,
    title: toStringValue(source.title ?? source.Title),
    language: toStringValue(source.language ?? source.Language),
    uploadTime: toStringValue(source.uploadTime ?? source.UploadTime),
    price: Number(source.price ?? source.Price ?? 0),
    status: toStringValue(source.status ?? source.Status),
    userId,
    videoIds: toIntArray(source.videoIds ?? source.VideoIds),
    imageIds: toIntArray(source.imageIds ?? source.ImageIds),
    subtitleIds: toIntArray(source.subtitleIds ?? source.SubtitleIds),
    addedAt: toStringValue(source.addedAt ?? source.AddedAt),
  };
}

function resolveAssetUploadId(asset: Asset): number {
  if (typeof asset.assetUploadId === "number" && asset.assetUploadId > 0) {
    return asset.assetUploadId;
  }
  return toInt(asset.id);
}

export async function getWatchlist(userId: number): Promise<WatchlistDto[]> {
  const res = await watchlistApi.getApiWatchlistUserId(userId);
  const rows = Array.isArray(res.data) ? res.data : [];
  return rows.map((row) => mapWatchlistRow(userId, row));
}

export async function addToWatchlist(
  userId: number,
  asset: Asset
): Promise<WatchlistDto | null> {
  const assetUploadId = resolveAssetUploadId(asset);
  if (assetUploadId <= 0) return null;

  await watchlistApi.postApiWatchlistUserIdAddAssetId(userId, assetUploadId);

  const list = await getWatchlist(userId);
  return list.find((row) => row.assetUploadId === assetUploadId) ?? null;
}

export type WatchlistRemoveRef =
  | { rowId: number }
  | { catalogAssetId: string };

export async function removeFromWatchlist(
  userId: number,
  ref: WatchlistRemoveRef
): Promise<boolean> {
  const assetUploadId =
    "rowId" in ref ? toInt(ref.rowId) : toInt(ref.catalogAssetId);

  if (assetUploadId <= 0) return false;

  await watchlistApi.deleteApiWatchlistUserIdRemoveAssetId(userId, assetUploadId);
  return true;
}